// IntegrationsPage.jsx — CONTAINER (dono da lógica). Monta { vm, actions } reais e renderiza a
// IntegrationsView (View pura do Claude Design). Formato View/ViewModel à risca (docs/DESIGN-FLOW.md).
// O visual é da View; aqui só há lógica/dados. Backup do legado: IntegrationsPage.legacy.jsx.bak.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import whatsappService from '../services/whatsappService';
import billingService from '../services/billingService';
import { useMeter } from '../hooks/useEntitlements';
import useSnackbar from '../hooks/useSnackbar';
import AppSnackbar from '../components/ui/AppSnackbar';
import PixQrCheckout from '../components/billing/PixQrCheckout';
import PackCpfDialog from '../components/billing/PackCpfDialog';
import OwnerPhoneDialog from '../components/integrations/OwnerPhoneDialog';
import usePackPurchase from '../hooks/usePackPurchase';
import IntegrationsView from './integrations/IntegrationsView';
import { devePedirTelefone, telefoneServeParaEnvio } from './integrations/ativacaoDoDono';

// ── helpers de formatação (a View recebe strings prontas — não formata) ──────────
const hourToTime = (h) => `${String(h ?? 0).padStart(2, '0')}:00`;
const timeToHour = (t) => parseInt(String(t || '0').split(':')[0], 10) || 0;
const fmtBRL = (cents) => `R$ ${((cents ?? 0) / 100).toFixed(2).replace('.', ',')}`;
const fmtDate = (iso) => (iso
  ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '—');
const DIRECTION_MAP = { outbound: 'out', inbound: 'in' };
const PURPOSE_LABEL = {
  created: 'Agendamento criado',
  confirmation_request: 'Confirmação',
  reminder: 'Lembrete',
  cancelled: 'Cancelamento',
  reply: 'Resposta automática',
  inbound: 'Resposta do cliente',
  owner_created: 'Aviso ao dono — novo agendamento',
  owner_confirmed: 'Aviso ao dono — confirmação',
  owner_cancelled: 'Aviso ao dono — cancelamento',
};
const STATUS_LABEL = { sent: 'Enviada', delivered: 'Entregue', read: 'Lida', failed: 'Falhou', undelivered: 'Não entregue', received: 'Recebida' };
const SOURCE_LABEL = { pack_purchase: 'Compra', plan_grant: 'Cota do plano', admin_grant: 'Cortesia / promoção', welcome_grant: 'Bônus de boas-vindas' };
const INTENT_STATUS = {
  fulfilled: { label: 'Pago', tone: 'paid' },
  pending:   { label: 'Aguardando pagamento', tone: 'pending' },
  failed:    { label: 'Falhou', tone: 'failed' },
  reversed:  { label: 'Estornado', tone: 'failed' },
};
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—');
// "+5511912345678" / "+551131234567" → "(11) 91234-5678" / "(11) 3123-4567"; formato inesperado → cru.
// ⚠️ Exemplo FICTÍCIO de propósito. Número real de alguém em comentário vira placeholder copiado
//    adiante — foi o que aconteceu com o celular do André, que se espalhou por 9 arquivos.
const fmtPhoneBR = (e164) => {
  const d = String(e164 ?? '').replace(/\D/g, '');
  if (!d) return '';
  const local = d.startsWith('55') && d.length >= 12 ? d.slice(2) : d;
  if (local.length === 11) return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  if (local.length === 10) return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  return e164 ?? '';
};
const fmtDayShort = (isoDate) => { const [, m, d] = String(isoDate).split('-'); return `${d}/${m}`; };

const DEFAULT_SETTINGS = {
  enabled: false, templateStyle: 2, messageNotes: '',
  notifyOnPublicCreate: true, notifyOnPrivateCreate: true,
  reminderEnabled: false, reminderHoursBefore: 24, reminderRespectBusinessHours: true,
  reminderBusinessStart: 8, reminderBusinessEnd: 18,
  ownerNotifyEnabled: false, ownerPhone: '',
  ownerNotifyOnCreate: true, ownerNotifyOnConfirm: true, ownerNotifyOnCancel: true,
};

const FALLBACK_SAMPLE = `Oi, Maria! 😊 Deu tudo certo, o seu horário está confirmado!

✨ Serviço: Corte de cabelo
💈 Profissional: Ana
📍 Local: Salão X — Rua Y, 123
🗓️ Quando: 25/06/2026 às 14:30

Precisa mudar alguma coisa? É só acessar app.agendario.com.br/a/123.

Até logo! 💚`;

const clampHour = (v, fb) => { const n = parseInt(v, 10); return Number.isNaN(n) ? fb : Math.min(24, Math.max(0, n)); };

function buildPayload(src) {
  return {
    ...src,
    messageNotes: src.messageNotes?.trim() ? src.messageNotes.trim() : null,
    reminderHoursBefore: Math.min(168, Math.max(1, parseInt(src.reminderHoursBefore, 10) || 24)),
    reminderBusinessStart: clampHour(src.reminderBusinessStart, 8),
    reminderBusinessEnd: clampHour(src.reminderBusinessEnd, 18),
  };
}

function buildPreview(model, notes) {
  const trimmed = notes?.trim();
  if (!model) return FALLBACK_SAMPLE;
  return trimmed
    ? (model.previewCreatedWithNotes || '').replace('{{ORIENTACOES}}', trimmed)
    : (model.previewCreated || '');
}

export default function IntegrationsPage() {
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { balance: waBalance, low: waLow, loading: waLoading } = useMeter('whatsapp_messages');

  // ── settings ─────────────────────────────────────────────────────────────────
  const [form, setForm] = useState(DEFAULT_SETTINGS);
  const [savedForm, setSavedForm] = useState(DEFAULT_SETTINGS);
  const [dirty, setDirty] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [togglingEnabled, setTogglingEnabled] = useState(false);
  const [models, setModels] = useState([]);

  // ── mensagens (histórico) ────────────────────────────────────────────────────
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messagesError, setMessagesError] = useState(null);
  const [summary, setSummary] = useState(null);

  // ── extrato de créditos (@v3) ────────────────────────────────────────────────
  const [statement, setStatement] = useState(null);
  const [statementLoading, setStatementLoading] = useState(true);
  const [statementError, setStatementError] = useState(null);

  const loadStatement = useCallback(async () => {
    setStatementLoading(true); setStatementError(null);
    try { setStatement(await whatsappService.getCreditStatement(30)); }
    catch { setStatementError('Não foi possível carregar o extrato. Tente novamente.'); }
    finally { setStatementLoading(false); }
  }, []);
  useEffect(() => { loadStatement(); }, [loadStatement]);

  // ── pacotes + compra PIX ─────────────────────────────────────────────────────
  const [products, setProducts] = useState([]);
  // Compra PIX: fluxo compartilhado com /billing (plano 19) — createOrder → 400 pede CPF → PixQrCheckout.
  const compra = usePackPurchase({ onPaid: loadStatement, showError, showSuccess });

  // Load settings + models on mount
  useEffect(() => {
    (async () => {
      try {
        const data = await whatsappService.getSettings();
        const merged = { ...DEFAULT_SETTINGS, ...data, messageNotes: data.messageNotes ?? '', ownerPhone: data.ownerPhone ?? '' };
        setForm(merged);
        setSavedForm(merged);
        try { setModels(await whatsappService.getModels()); } catch { /* fallback */ }
      } catch { /* mantém defaults */ } finally { setLoadingSettings(false); }
    })();
  }, []);

  // Load products (pacotes)
  useEffect(() => {
    billingService.listProducts('whatsapp_messages')
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => { /* seção de pacotes fica vazia */ });
  }, []);

  // Load messages on page/rowsPerPage change
  const loadMessages = useCallback(async (p, rpp) => {
    setLoadingMessages(true);
    setMessagesError(null);
    try {
      const data = await whatsappService.listMessages(p + 1, rpp); // API é 1-indexed
      setMessages(data.items ?? []);
      setTotal(data.total ?? 0);
      setSummary(data.summary ?? null);
    } catch {
      setMessagesError('Não foi possível carregar o histórico. Tente novamente.');
    } finally { setLoadingMessages(false); }
  }, []);
  useEffect(() => { loadMessages(page, rowsPerPage); }, [page, rowsPerPage, loadMessages]);

  // ── handlers ─────────────────────────────────────────────────────────────────
  const updateField = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    setSavingSettings(true);
    try {
      await whatsappService.updateSettings(buildPayload(form));
      setSavedForm(form);
      setDirty(false);
      showSuccess('Configurações de WhatsApp salvas.');
    } catch { showError('Não foi possível salvar as configurações. Tente novamente.'); }
    finally { setSavingSettings(false); }
  }, [form, showSuccess, showError]);

  const handleDiscard = useCallback(() => { setForm(savedForm); setDirty(false); }, [savedForm]);

  // ── 1ª ativação: pedir o WhatsApp do dono ────────────────────────────────────
  //
  // Medido em PRD (06/10/2026): 85 das 114 contas ativas não têm telefone do dono — as MESMAS 85
  // que estão sem o aviso ao dono. A opção existe, mas numa seção que ninguém abre depois de
  // ativar. Aqui é o único momento em que a pessoa está olhando para isso.
  const [pedindoTelefone, setPedindoTelefone] = useState(false);
  const [telefoneDoDono, setTelefoneDoDono] = useState('');
  const [erroDoTelefone, setErroDoTelefone] = useState(null);

  /**
   * Liga a integração. `extras` carrega o telefone quando vem da modal.
   *
   * 🔑 Um PUT só, com tudo junto. Dois PUTs (ligar, depois gravar o telefone) deixariam a conta
   *    ativada e sem número se o segundo falhasse — exatamente o estado que esta tela existe
   *    para evitar.
   */
  const ativarDeFato = useCallback(async (next, extras = {}) => {
    setForm((f) => ({ ...f, enabled: next, ...extras }));
    setTogglingEnabled(true);
    try {
      await whatsappService.updateSettings(buildPayload({ ...savedForm, ...extras, enabled: next }));
      setSavedForm((s) => ({ ...s, ...extras, enabled: next }));
      if (next) {
        // 1ª ativação concede 50 créditos no backend; sem refetch a tela expande com o
        // snapshot velho (saldo 0) e o usuário só vê o bônus após F5 — inclusive comprando à toa.
        queryClient.invalidateQueries({ queryKey: ['entitlements'] });
        loadStatement();
      }
      showSuccess(
        !next ? 'WhatsApp desativado.'
          : extras.ownerPhone ? 'WhatsApp ativado. Você também vai receber os avisos.'
            : 'WhatsApp ativado.');
      return true;
    } catch {
      setForm((f) => ({ ...f, enabled: savedForm.enabled }));
      showError('Não foi possível atualizar. Tente novamente.');
      return false;
    } finally { setTogglingEnabled(false); }
  }, [savedForm, showSuccess, showError, queryClient, loadStatement]);

  /**
   * O gatilho da tela. Só intercepta quando está LIGANDO e não há telefone salvo — desligar,
   * religar com número já guardado ou qualquer outro caminho segue direto, como sempre foi.
   */
  const handleToggleEnabled = useCallback(async (next) => {
    if (devePedirTelefone(next, savedForm.ownerPhone)) {
      setTelefoneDoDono('');
      setErroDoTelefone(null);
      setPedindoTelefone(true);
      return;
    }
    await ativarDeFato(next);
  }, [savedForm.ownerPhone, ativarDeFato]);

  const confirmarTelefone = useCallback(async () => {
    if (!telefoneServeParaEnvio(telefoneDoDono)) {
      setErroDoTelefone('Digite o número com DDD, como (11) 91234-5678.');
      return;
    }
    // ownerNotifyEnabled junto: telefone sem o opt-in não enviaria nada, e a pessoa acabou de
    // dizer que quer receber. Pedir duas confirmações para a mesma intenção é atrito à toa.
    const ok = await ativarDeFato(true, { ownerPhone: telefoneDoDono, ownerNotifyEnabled: true });
    if (ok) setPedindoTelefone(false);
  }, [telefoneDoDono, ativarDeFato]);

  /** Pular ativa do mesmo jeito, sem telefone — é o comportamento que existia antes desta modal. */
  const pularTelefone = useCallback(async () => {
    setPedindoTelefone(false);
    await ativarDeFato(true);
  }, [ativarDeFato]);


  // ── ViewModel ────────────────────────────────────────────────────────────────
  const viewState = loadingSettings ? 'loading' : (form.enabled ? 'enabled' : 'disabled');
  const level = (waBalance ?? 0) === 0 ? 'zero' : (waLow ? 'low' : 'ok');
  const previewText = useMemo(
    () => buildPreview(models.find((m) => m.id === form.templateStyle), form.messageNotes) || FALLBACK_SAMPLE,
    [models, form.templateStyle, form.messageNotes],
  );

  const vm = {
    viewState,
    errorMessage: null,
    whatsapp: { enabled: form.enabled },
    credits: {
      balance: waBalance ?? 0,
      balanceLabel: `${(waBalance ?? 0).toLocaleString('pt-BR')} créditos`,
      // @v3: snapshot de saldo ainda carregando — a View mostra skeleton em vez do falso "0 créditos".
      loading: waLoading,
      level,
      statement: {
        loading: statementLoading,
        errorMessage: statementError,
        isEmpty: !statementLoading && !statementError
          && !(statement?.entries?.length) && !(statement?.purchases?.length) && !(statement?.daily?.length),
        entries: (statement?.entries ?? []).map((e) => {
          const expired = e.expiresAt && new Date(e.expiresAt) <= new Date();
          const depleted = (e.remaining ?? 0) <= 0 && !expired;
          return {
            id: String(e.id),
            dateLabel: fmtDay(e.createdAt),
            originLabel: e.source === 'pack_purchase'
              ? `Compra — ${e.productName ?? 'Pacote'}`
              : (SOURCE_LABEL[e.source] ?? e.source),
            qtyLabel: `+${Number(e.qty ?? 0).toLocaleString('pt-BR')}`,
            remainingLabel: (e.remaining ?? 0) > 0 && !expired
              ? `${Number(e.remaining).toLocaleString('pt-BR')} restantes` : 'esgotado',
            validityLabel: e.expiresAt
              ? `${expired ? 'expirou' : 'expira'} ${fmtDay(e.expiresAt)}` : 'não expira',
            status: expired ? 'expired' : (depleted ? 'depleted' : 'active'),
          };
        }),
        purchases: (statement?.purchases ?? []).map((p) => {
          const st = INTENT_STATUS[p.status] ?? { label: p.status, tone: 'pending' };
          return {
            id: String(p.intentId),
            dateLabel: fmtDate(p.createdAt),
            packLabel: p.productName ?? 'Pacote',
            qtyLabel: p.creditQty ? `${Number(p.creditQty).toLocaleString('pt-BR')} mensagens` : '—',
            priceLabel: p.priceCents != null ? fmtBRL(p.priceCents) : '—',
            statusLabel: st.label,
            statusTone: st.tone,
          };
        }),
        usage: (() => {
          const days = (statement?.daily ?? []).map((d) => ({ dateLabel: fmtDayShort(d.date), consumed: d.consumed }));
          const totalConsumed = (statement?.daily ?? []).reduce((acc, d) => acc + (d.consumed ?? 0), 0);
          return {
            isEmpty: days.length === 0,
            days,
            maxConsumed: days.reduce((m, d) => Math.max(m, d.consumed), 0),
            totalLabel: days.length ? `${totalConsumed.toLocaleString('pt-BR')} mensagens nos últimos ${statement?.windowDays ?? 30} dias` : '',
          };
        })(),
      },
    },
    message: {
      models: models.length ? models : [{ id: 1, name: 'Modelo 1' }, { id: 2, name: 'Modelo 2' }],
      selectedModelId: form.templateStyle,
      freeText: form.messageNotes,
      previewBubbles: [{ from: 'business', text: previewText, time: '14:30', hasButtons: form.reminderEnabled }],
    },
    notifyOnCreate: { fromPublicSite: form.notifyOnPublicCreate, fromPanel: form.notifyOnPrivateCreate },
    reminder: {
      enabled: form.reminderEnabled,
      hoursBefore: Math.min(168, Math.max(1, parseInt(form.reminderHoursBefore, 10) || 24)),
      respectBusinessHours: form.reminderRespectBusinessHours,
      businessStart: hourToTime(form.reminderBusinessStart),
      businessEnd: hourToTime(form.reminderBusinessEnd),
    },
    ownerNotify: {
      enabled: form.ownerNotifyEnabled,
      phone: form.ownerPhone,
      phoneError: null,
      events: { newBooking: form.ownerNotifyOnCreate, clientConfirmed: form.ownerNotifyOnConfirm, clientCancelled: form.ownerNotifyOnCancel },
    },
    packages: products.map((p, i) => ({
      id: String(p.id),
      qty: Number(p.creditQty ?? 0),
      qtyLabel: `${Number(p.creditQty ?? 0).toLocaleString('pt-BR')} mensagens`,
      priceLabel: fmtBRL(p.priceCents),
      perMsgLabel: p.creditQty ? `${fmtBRL(p.priceCents / p.creditQty)} por mensagem` : '',
      validityLabel: p.validityDays ? `Validade ${p.validityDays} dias` : '',
      highlighted: products.length > 1 && i === Math.floor(products.length / 2),
    })),
    history: {
      isEmpty: !loadingMessages && total === 0 && !messagesError,
      summary: { sent: summary?.sent ?? 0, received: summary?.received ?? 0, failed: summary?.failedNoCredit ?? 0 },
      rows: messages.map((m, idx) => ({
        id: String(m.id ?? idx),
        dateLabel: fmtDate(m.createdAt),
        direction: DIRECTION_MAP[m.direction] ?? 'out',
        typeLabel: PURPOSE_LABEL[m.purpose] ?? m.purpose ?? '—',
        statusLabel: STATUS_LABEL[m.status] ?? m.status ?? '—',
        phoneMasked: m.phoneMasked,
        // @v3: telefone real (cliente do próprio dono), corpo da inbound e link p/ o cliente.
        phoneLabel: fmtPhoneBR(m.phone) || m.phoneMasked || '—',
        bodyPreview: m.bodyPreview ?? null,
        personId: m.personId ?? null,
        personName: m.personName ?? null,
        personHref: m.personId ? `/persons/${m.personId}` : null,
      })),
      page: page + 1,
      pageCount: Math.max(1, Math.ceil(total / rowsPerPage)),
      total,
      rowsPerPage,
      errorMessage: messagesError,
      loading: loadingMessages,
    },
    saving: savingSettings,
    dirty,
    togglingEnabled,
  };

  const actions = {
    onToggleWhatsapp: handleToggleEnabled,
    onActivate: () => handleToggleEnabled(true),
    onSelectModel: (id) => updateField('templateStyle', id),
    onChangeFreeText: (t) => updateField('messageNotes', t),
    onToggleNotifyOnCreate: (key, next) => updateField(key === 'fromPublicSite' ? 'notifyOnPublicCreate' : 'notifyOnPrivateCreate', next),
    onToggleReminder: (next) => updateField('reminderEnabled', next),
    onChangeReminderHours: (h) => updateField('reminderHoursBefore', h),
    onToggleRespectBusinessHours: (next) => updateField('reminderRespectBusinessHours', next),
    onChangeBusinessHours: (start, end) => { updateField('reminderBusinessStart', timeToHour(start)); updateField('reminderBusinessEnd', timeToHour(end)); },
    onToggleOwnerNotify: (next) => updateField('ownerNotifyEnabled', next),
    onChangeOwnerPhone: (p) => updateField('ownerPhone', p),
    onToggleOwnerEvent: (key, next) => updateField({ newBooking: 'ownerNotifyOnCreate', clientConfirmed: 'ownerNotifyOnConfirm', clientCancelled: 'ownerNotifyOnCancel' }[key], next),
    onBuyPackage: (id) => { const p = products.find((x) => String(x.id) === String(id)); if (p) compra.handleBuy(p); },
    onChangeHistoryPage: (pg) => setPage(Math.max(0, pg - 1)),
    onChangeHistoryRowsPerPage: (rpp) => { setRowsPerPage(rpp); setPage(0); },
    onRefreshHistory: () => loadMessages(page, rowsPerPage),
    onDismissHistoryError: () => setMessagesError(null),
    onRetryStatement: loadStatement,
    onSave: handleSave,
    onDiscard: handleDiscard,
  };

  return (
    <>
      <IntegrationsView vm={vm} actions={actions} />

      {/* 1ª ativação sem telefone do dono: pede o número antes de ligar (dá para pular) */}
      <OwnerPhoneDialog
        open={pedindoTelefone}
        phone={telefoneDoDono}
        setPhone={(v) => { setTelefoneDoDono(v); if (erroDoTelefone) setErroDoTelefone(null); }}
        phoneError={erroDoTelefone}
        submitting={togglingEnabled}
        onSkip={pularTelefone}
        onSubmit={confirmarTelefone}
      />

      {/* Modal de CPF (1ª compra sem CPF salvo) + checkout PIX — compartilhados com /billing */}
      <PackCpfDialog {...compra.cpfDialog} />
      {compra.checkoutOrder && <PixQrCheckout order={compra.checkoutOrder} onClose={compra.closeCheckout} />}
      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </>
  );
}
